import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useContext, useEffect, useState } from 'react';
import classNames from 'classnames';
import OutsideClickHandler from 'react-outside-click-handler';
import threeDotsLogo from '../../../assets/images/threeDots.png';
import Button from '../../common/Button/Button';
import styles from './RepresentativeTable.module.css';
import Customer from '../../../models/Customer';
import {
  deleteRepresentative,
  editRepresentative,
  getRepresentativesForCustomer,
} from '../../../services/representative/representativeService';
import CustomerContext from '../../../context/customer-context';
import Modal from '../../common/Modal/Modal';
import APIError from '../../../services/APIError';

type RepresentativeTableProps = {
  customer: Customer
}

type Representative = {
  id: number,
  representative: Customer,
  relationDescription: string
}

function RepresentativeTable(props: RepresentativeTableProps) {
  const customerCtx = useContext(CustomerContext);
  const navigation = useNavigate();
  const { t } = useTranslation();
  const [representatives, setRepresentatives] = useState<Representative[]>([]);
  const [showOptionsId, setShowOptionsId] = useState<number>();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [relationToDeleteId, setRelationToDeleteId] = useState<number>();
  const [editRoleId, setEditRoleId] = useState<number>();
  const [relationToEdit, setRelationToEdit] = useState<Customer>();
  const [relationDescriptionEdit, setRelationDescriptionEdit] = useState<string>();
  const [relationInputError, setRelationInputError] = useState(false);

  const getRepresentatives = async () => {
    const result = await getRepresentativesForCustomer({ id: props.customer.id });
    const representativeList = result.map(relation => (
      {
        id: relation.id!,
        representative: relation.representative,
        relationDescription: relation.relationDescription,
      }
    ));
    setRepresentatives(representativeList);
  };

  useEffect(() => {
    getRepresentatives();
  }, [props.customer]);

  const removeRepresentative = async (id: number) => {
    try {
      await deleteRepresentative({ relationId: id });
      getRepresentatives();
    } catch (err) {
      if (err instanceof APIError) {
        console.log(`Failed to delete representative: ${err.errorBody}`);
      } else {
        console.log(err);
      }
    }
  };

  const editRepresentativeRole = async (id: number, representative: Customer) => {
    try {
      await editRepresentative({
        id,
        customer: props.customer,
        representative,
        relationDescription: relationDescriptionEdit,
      });
      getRepresentatives();
    } catch (err) {
      if (err instanceof APIError) {
        console.log(`Failed to edit representative: ${err.errorBody}`);
      } else {
        console.log(err);
      }
    }
  };

  const handleDelete = () => {
    if (relationToDeleteId) {
      removeRepresentative(relationToDeleteId);
      setShowDeleteModal(false);
    }
  };

  const handleRemoveClick = () => {
    setRelationToDeleteId(showOptionsId);
    setShowDeleteModal(true);
    setShowOptionsId(undefined);
  };

  const handleEditClick = (representative: Customer, roleId: number) => {
    setRelationToEdit(representative);
    setEditRoleId(roleId);
    setShowOptionsId(undefined);
  };

  const roleInputUpdate = (event: any) => {
    event.preventDefault();
    setRelationDescriptionEdit(event.target.value);
  };

  const handleConfirmEdit = () => {
    if (editRoleId && relationToEdit && relationDescriptionEdit) {
      editRepresentativeRole(editRoleId, relationToEdit);
      setEditRoleId(undefined);
    } else {
      setRelationInputError(true);
    }
  };

  const handleCancelEdit = () => {
    setRelationDescriptionEdit(undefined);
    setRelationToEdit(undefined);
    setEditRoleId(undefined);
  };

  const handleAddRepresentative = () => {
    navigation('/customer/representative');
  };

  const openRepresentative = (representative: Customer) => {
    customerCtx.setCustomer(representative);
    navigation('/customer');
    window.scrollTo(0, 0);
  };

  return (
    <>
      {showDeleteModal && (
        <Modal onClose={() => setShowDeleteModal(false)}>
          <div className={styles.modalContainer}>
            <p>{t('customer.dashboard.representativeInformation.deleteModalMessage')}</p>
            <div className={styles.buttonContainer}>
              <button
                type='button'
                className={styles.button}
                onClick={handleDelete}
              >
                {t('common.remove')}
              </button>
              <button
                type='button'
                className={styles.button}
                onClick={() => setShowDeleteModal(false)}
              >
                {t('common.cancel')}
              </button>
            </div>
          </div>
        </Modal>
      )}
      <div>
        <div>
          <table className={styles.table}>
            <thead>
            <tr className={styles.row}>
              <th>{t('customer.dashboard.representativeInformation.header.representative')}</th>
              <th>{t('customer.dashboard.representativeInformation.header.id')}</th>
              <th>{t('customer.dashboard.representativeInformation.header.cif')}</th>
              <th>{t('customer.dashboard.representativeInformation.header.login')}</th>
              <th>{t('customer.dashboard.representativeInformation.header.role')}</th>
              <th>
                <div className={styles.options}>
                  {t('customer.dashboard.representativeInformation.header.action')}
                </div>
              </th>
            </tr>
            </thead>
            <tbody>
            {representatives.map(person => (
                <tr key={person.representative.id}>
                  <td>
                  <span
                    role='button'
                    tabIndex={0}
                    onClick={() => openRepresentative(person.representative)}
                    onKeyDown={() => openRepresentative(person.representative)}
                    className={styles.name}
                  >
                    {person.representative.firstName}
                    {' '}
                    {person.representative.name}
                  </span>
                  </td>
                  <td>{person.representative.registrationCode}</td>
                  <td>{person.representative.cif}</td>
                  <td>{person.representative.loginName}</td>
                  <td>
                    {editRoleId === person.id
                      ? (
                        <div className={styles.inputContainer}>
                          <input
                            id='role'
                            className={
                              classNames(styles.input, relationInputError && styles.inputError)
                            }
                            placeholder={t('representative.view.placeholder.role')}
                            maxLength={100}
                            type='text'
                            onChange={roleInputUpdate}
                          />
                          <button
                            type='button'
                            className={styles.confirmEditButton}
                            onClick={handleConfirmEdit}
                          >
                            Confirm
                          </button>
                          <button
                            type='button'
                            className={styles.cancelEditButton}
                            onClick={handleCancelEdit}
                          >
                            Cancel
                          </button>
                        </div>)
                      : <span className={styles.role}>{person.relationDescription}</span>}
                  </td>
                  <td>
                    <div
                      role='button'
                      tabIndex={0}
                      onClick={() => setShowOptionsId(person.id)}
                      onKeyDown={() => setShowOptionsId(person.id)}
                      className={styles.options}
                    >
                      <img
                        src={threeDotsLogo}
                        alt={t('customer.dashboard.representativeInformation.threeDotsLogo')}
                        className={styles.logo}
                      />
                    </div>
                    {showOptionsId === person.id && (
                      <div className={styles.optionsContainer}>
                        <OutsideClickHandler
                          onOutsideClick={() => setShowOptionsId(undefined)}
                          display='inline-block'
                        >
                          <div className={styles.optionsList}>
                            <div
                              role='button'
                              tabIndex={0}
                              className={styles.option}
                              onClick={() => handleEditClick(person.representative, person.id)}
                              onKeyDown={() => handleEditClick(person.representative, person.id)}
                            >
                              <span className={styles.optionText}>{t('common.edit')}</span>
                            </div>
                            <div
                              role='button'
                              tabIndex={0}
                              className={styles.option}
                              onClick={handleRemoveClick}
                              onKeyDown={handleRemoveClick}
                            >
                              <span className={styles.optionText}>{t('common.remove')}</span>
                            </div>
                          </div>
                        </OutsideClickHandler>
                      </div>
                    )}
                  </td>
                </tr>
              ),
            )}
            </tbody>
          </table>
          <div className={styles.addButton}>
            <Button
              handleClick={handleAddRepresentative}
              text={t('customer.dashboard.representativeInformation.addRepresentative')}
            />
          </div>
        </div>
      </div>
    </>
  );
}

export default RepresentativeTable;
